import React from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import {
  Button,
  ButtonKind,
  DeleteIcon,
  IconButton,
  IconButtonKind,
  InputLabel,
  InputMessage,
  InputMessageKind,
  Modal,
  ModalKind,
  PlusSignIcon,
  toast,
  TextInput,
} from '@aposphaere/ui-components'
import { useModal } from '../../contexts/modalContext'
import { useGetSamples } from '../../hooks/graphql'
import { useAuthenticatedMutation } from '../../hooks/useAuthenticatedMutation'
import { createHandover } from '@aposphaere/core-kit'

type SampleForm = {
  sampleType: string
  amount: string
}

type PersonForm = {
  fullName: string
  privacyAccepted: boolean
  samples: SampleForm[]
}

interface ProductGiveawayFormValues {
  persons: PersonForm[]
}

const createEmptySample = (): SampleForm => ({
  sampleType: '',
  amount: '',
})

const createEmptyPerson = (): PersonForm => ({
  fullName: '',
  privacyAccepted: false,
  samples: [createEmptySample()],
})

const ProductGiveawaySchema = Yup.object().shape({
  persons: Yup.array().of(
    Yup.object().shape({
      fullName: Yup.string().required('Pflichtfeld'),
      privacyAccepted: Yup.bool().oneOf([true], 'Pflichtfeldn'),
      samples: Yup.array().of(
        Yup.object().shape({
          sampleType: Yup.string().required('Pflichtfeld'),
          amount: Yup.string().required('Pflichtfeld'),
        }),
      ),
    }),
  ),
})

interface Props {
  pharmacyId: number
}

const ProductGiveawayModal: React.FC<Props> = ({ pharmacyId }) => {
  const { closeModal } = useModal()
  const { data: samples } = useGetSamples()
  const createHandoverMutation = useAuthenticatedMutation(createHandover)

  const initialValues: ProductGiveawayFormValues = {
    persons: [createEmptyPerson()],
  }

  const mapFormToHandovers = (values: ProductGiveawayFormValues, samples: any[]) => {
    return values.persons.map((person) => ({
      recipient: person.fullName,
      samples: person.samples.map((s) => {
        const sampleObject = samples.find((x) => String(x.id) === String(s.sampleType))

        return {
          sample_id: sampleObject?.id ?? null,
          amount: Number(s.amount),
        }
      }),
    }))
  }

  const getMutationWithVariables = (values: ProductGiveawayFormValues) => {
    const handovers = mapFormToHandovers(values, samples ?? [])

    const variables = {
      pharmacy_id: pharmacyId,
      handovers,
    }

    return { mutation: createHandoverMutation, variables }
  }

  const handleSubmit = async (values: ProductGiveawayFormValues) => {
    const { mutation, variables } = getMutationWithVariables(values)

    const response = await mutation(variables)

    if (response.errors) {
      alert(response.errors)
      return
    }

    toast.show({
      headline: 'Anfrage erfolgreich gesendet',
      type: 'success',
    })

    closeModal()
  }

  return (
    <Modal kind={ModalKind.ms} title="Abgabe Produkt" onClose={closeModal} onBack={() => null}>
      <Formik<ProductGiveawayFormValues> initialValues={initialValues} validationSchema={ProductGiveawaySchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => {
          const isAnyError = Object.keys(errors).length > 0

          return (
            <div className="flex flex-col w-full">
              <div className="w-full p-4 space-y-8">
                {values.persons.map((person, personIndex) => {
                  const personErrors = (errors.persons && (errors.persons as any)[personIndex]) || undefined
                  const personTouched = (touched.persons && (touched.persons as any)[personIndex]) || undefined

                  return (
                    <div key={personIndex} className="relative flex w-full border border-gray-200 rounded-md p-6 gap-6">
                      {values.persons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...values.persons]
                            updated.splice(personIndex, 1)
                            setFieldValue('persons', updated)
                          }}
                          className="
                          absolute top-2 right-2
                          h-fit items-center
                          px-1 py-0.5
                          rounded-md
                          text-gray-700
                          hover:bg-blue-200 hover:text-blue-700
                          active:text-blue-800
                          focus:outline-none
                          transition ease-in-out duration-150
                        "
                        >
                          ✕
                        </button>
                      )}
                      <div className="w-1/3">
                        <InputLabel>{`Person ${personIndex + 1}`}</InputLabel>

                        <div className="mt-1 rounded-md relative">
                          <TextInput
                            type="text"
                            value={person.fullName}
                            onChange={(event: React.FormEvent<HTMLInputElement>) =>
                              setFieldValue(`persons[${personIndex}].fullName`, event.currentTarget.value)
                            }
                          />

                          {personErrors?.fullName && personTouched?.fullName && (
                            <InputMessage kind={InputMessageKind.error} className="absolute left-0 top-full mt-0.5">
                              {personErrors.fullName as string}
                            </InputMessage>
                          )}
                        </div>
                      </div>

                      <div className="w-1/2">
                        <div className="space-y-4">
                          {person.samples.map((sample, sampleIndex) => {
                            const sampleErrors = personErrors?.samples && personErrors.samples[sampleIndex]
                            const sampleTouched = personTouched?.samples && personTouched.samples[sampleIndex]

                            return (
                              <div key={sampleIndex} className="flex items-end gap-3">
                                <div className="flex items-start gap-2">
                                  <div className="relative" style={{ width: '230px' }}>
                                    <InputLabel className="mb-1">Sample</InputLabel>
                                    <select
                                      value={sample.sampleType}
                                      onChange={(event: React.FormEvent<HTMLSelectElement>) =>
                                        setFieldValue(`persons[${personIndex}].samples[${sampleIndex}].sampleType`, event.currentTarget.value)
                                      }
                                      className="w-full form-select font-body h-10 text-gray-900 bg-gray-100 block rounded-md pr-10 py-2 text-base leading-6 border outline-none focus:shadow-focus border-gray-400 focus:border-blue-400"
                                    >
                                      <option value="">Bitte wählen</option>

                                      {samples?.map((item) => (
                                        <option key={item.id} value={item.id}>
                                          {item.name}
                                        </option>
                                      ))}
                                    </select>

                                    {sampleErrors?.sampleType && sampleTouched?.sampleType && (
                                      <InputMessage kind={InputMessageKind.error} className="absolute left-0 top-full mt-0.5">
                                        {sampleErrors.sampleType as string}
                                      </InputMessage>
                                    )}
                                  </div>
                                  <div className="w-28 relative">
                                    <InputLabel className="mb-1">Menge</InputLabel>

                                    <TextInput
                                      type="number"
                                      value={sample.amount}
                                      onChange={(event: React.FormEvent<HTMLInputElement>) =>
                                        setFieldValue(`persons[${personIndex}].samples[${sampleIndex}].amount`, event.currentTarget.value)
                                      }
                                    />

                                    {sampleErrors?.amount && sampleTouched?.amount && (
                                      <InputMessage kind={InputMessageKind.error} className="absolute left-0 top-full mt-0.5">
                                        {sampleErrors.amount as string}
                                      </InputMessage>
                                    )}
                                  </div>
                                </div>

                                <div className="flex flex-col items-center justify-end">
                                  {person.samples.length > 1 ? (
                                    <IconButton
                                      kind={IconButtonKind.custom}
                                      icon={<DeleteIcon />}
                                      additionalCss="h-fit items-center p-1px ml-3 rounded-md text-gray-700 border border-gray-400 hover:bg-blue-200 hover:text-blue-700"
                                      onClick={() => {
                                        const updated = [...person.samples]
                                        updated.splice(sampleIndex, 1)
                                        setFieldValue(`persons[${personIndex}].samples`, updated)
                                      }}
                                    />
                                  ) : (
                                    <div className="h-[32px]" />
                                  )}

                                  {((sampleErrors?.sampleType && sampleTouched?.sampleType) || (sampleErrors?.amount && sampleTouched?.amount)) && (
                                    <div className="min-h-[20px] opacity-0 pointer-events-none">Pflichtfeld</div>
                                  )}
                                </div>
                              </div>
                            )
                          })}

                          <Button
                            kind={ButtonKind.outlinedSecondary}
                            icon={<PlusSignIcon />}
                            onClick={() => setFieldValue(`persons[${personIndex}].samples`, [...person.samples, createEmptySample()])}
                          >
                            Sample hinzufügen
                          </Button>
                        </div>
                      </div>

                      <div className="w-1/6 flex items-start justify-end">
                        <div className="relative pt-6">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={person.privacyAccepted}
                              onChange={(event: React.FormEvent<HTMLInputElement>) =>
                                setFieldValue(`persons[${personIndex}].privacyAccepted`, event.currentTarget.checked)
                              }
                            />
                            <span className="text-sm">Datenschutz akzeptieren</span>
                          </label>

                          {personErrors?.privacyAccepted && personTouched?.privacyAccepted && (
                            <InputMessage kind={InputMessageKind.error} className="absolute left-0 top-full mt-0.5 whitespace-nowrap">
                              {personErrors.privacyAccepted as string}
                            </InputMessage>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="w-full px-4 pb-4">
                <Button
                  kind={ButtonKind.outlinedSecondary}
                  icon={<PlusSignIcon />}
                  onClick={() => setFieldValue('persons', [...values.persons, createEmptyPerson()])}
                >
                  Person hinzufügen
                </Button>
              </div>

              <div className="flex sticky bg-gradient-to-t from-white via-white to-transparent-opacity-0 self-end bottom-0 w-full justify-between p-4 pt-8 pb-6">
                <Button kind={ButtonKind.outlinedSecondary} onClick={closeModal}>
                  Abbrechen
                </Button>
                <Button kind={ButtonKind.primary} onClick={handleSubmit} disabled={isSubmitting}>
                  Anfrage senden
                </Button>
              </div>
            </div>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default ProductGiveawayModal
